import { useTenantStore } from "@/store/tenant";
import { defineAsyncComponent, markRaw, computed, ComponentPublicInstance, ComputedRef, Ref } from 'vue';

export interface ComponentList { 
  'blue-default-GameType'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'blue-default-Platform'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'blue-default-v01-GameType'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'blue-default-v02-Platform'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'amber-purple-v01-Platform'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'green-default-v01-GameType'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'green-default-v01-Platform'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'green-default-v02-Platform'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'forest-green-v01-Platform'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
  'forest-green-v02-Platform'?: () => Promise<ReturnType<typeof defineAsyncComponent>>;
}

export const useLoadComponent = () => {
  const tenantStore = useTenantStore();

  const {
    themeConfig
  } = toRefs(tenantStore);

  const themeHomeType = computed(() => {
    const { theme, home, homeType } = tenantStore.themeConfig ?? {};
    return  `${theme}-${home ? (home + '-') : ''}${homeType ?? 'GameType'}`;
  });

  // 组件加载器
  const loadComponent = (list: ComponentList) => {
    const components = list[themeHomeType.value as keyof ComponentList] || list['blue-default-GameType'] || null;
    if (components) {
      const asyncComponent = defineAsyncComponent(components);
      return markRaw(asyncComponent) as unknown as ComponentPublicInstance | null;
    }
    return null;
  };

  return {
    themeConfig,
    loadComponent,
    themeHomeType,
  };
}

export const useHeaderToolbar = (supportLanguages: ComputedRef<string[]> | Ref<string[]>) => {
  const { themeHomeType } = useLoadComponent();
  
  const headerLanguageHideList = ['green-default-v02-Platform', 'forest-green-v02-Platform'];
  
  type BtnKey = 'search' | 'language';

  const hideButton = (key: BtnKey): boolean => {
    const btns: Record<BtnKey, () => boolean> = {
      search: () => false,
      language: () => {
        return supportLanguages.value.length > 1 && !headerLanguageHideList.includes(themeHomeType.value);
      },
    };
    return btns[key]();
  }

  const hideList = ['amber-purple-GameType']

  // 判断是否显示资产图标
  const showAssetsIcon = computed(() => {
    return !hideList.includes(themeHomeType.value)
  })

  return {
    hideButton,
    showAssetsIcon,
  }
}

export const useGradientSVG = () => {
  const { themeHomeType } = useLoadComponent();
  
  const svgThemeKey: Record<string, string[]> = {
    '1': ['green-default-v01-Platform', 'green-default-v02-Platform', 'forest-green-v01-Platform', 'forest-green-v02-Platform'],
    '2': ['blue-default-v02-Platform', 'blue-default-Platform', 'blue-default-v01-GameType'],
    '3': ['amber-purple-v01-Platform'],
  }

  const selectSvgGradientID = () => {
    const list = Object.keys(svgThemeKey);
    for (const key of list) {
      if (svgThemeKey[key].includes(themeHomeType.value)) {
        return key;
      }
    }
  }
  return {
    selectSvgGradientID,
  }
}

export const usePwaToolbar = () => {
  const { themeHomeType } = useLoadComponent();

  const pwaHeaderCloseBtnInStart = ['amber-purple-v01-Platform', 'blue-default-v02-Platform', 'green-default-v01-Platform', 'green-default-v02-Platform', 'forest-green-v01-Platform', 'forest-green-v02-Platform']
  
  const closeButtonPosition = computed(() => {
    return pwaHeaderCloseBtnInStart.includes(themeHomeType.value) ? 'start' : 'end'
  }); // 关闭按钮

  return {
    closeButtonPosition,
  }
}

export const useGameCard = () => {
  const { themeHomeType } = useLoadComponent();

  const gameHeadTypes: Record<string, any> = {
    'blue-default-GameType': () => ({
      titleType: '2',
      headType: '1',
      size: 6,
      row: 2,
      hotSize: 9,
      hotRow: 3,
    }),
    'blue-default-Platform': () => ({
      titleType: '1',
      headType: '3',
      isShowAll: true,
      size: 12,
      row: 3,
      hotSize: 16,
      hotRow: 4,
    }),
    'blue-default-v01-GameType': () => ({
      titleType: '1',
      headType: '6',
      size: 16,
      row: 4,
      hotSize: 16,
      hotRow: 4,
    }),
    'green-default-v01-GameType': () => ({
      titleType: '1',
      headType: '1',
      size: 12,
      row: 3,
      hotSize: 16,
      hotRow: 4,
    }),
    'green-default-v01-Platform': () => ({
      titleType: '1',
      headType: '4',
      isShowAll: true,
      size: 12,
      row: 3,
      hotSize: 16,
      hotRow: 4,
    }),
    'amber-purple-v01-Platform': () => ({
      titleType: '1',
      headType: '4',
      isShowAll: true,
      size: 12,
      row: 3,
      hotSize: 16,
      hotRow: 4,
    }),
    'blue-default-v02-Platform': () => ({
      titleType: '1',
      headType: '4',
      isShowAll: true,
      size: 12,
      row: 3,
      hotSize: 16,
      hotRow: 4,
    }),
    'green-default-v02-Platform': () => ({
      titleType: '1',
      headType: '5',
      isShowAll: true,
      size: 9,
      row: 3,
      hotSize: 12,
      hotRow: 4,
    }),
    'forest-green-v01-Platform': () => ({
      titleType: '1',
      headType: '4',
      isShowAll: true,
      size: 12,
      row: 3,
      hotSize: 16,
      hotRow: 4,
    }),
    'forest-green-v02-Platform': () => ({
      titleType: '1',
      headType: '5',
      isShowAll: true,
      size: 9,
      row: 3,
      hotSize: 12,
      hotRow: 4,
    }),
  }

  // 游戏卡片参数
  const gameWrapperOptions = computed(() => {
    const headType = gameHeadTypes[themeHomeType.value] || gameHeadTypes['blue-default-GameType'];
    return headType()
  });

  return {
    gameWrapperOptions
  }
}

export const useHotGameCard = () => {
  const { themeHomeType } = useLoadComponent();

  const themeLogos: Record<string, any> = {
    'image': {
      logo1: '/icons/sort/POPULAR_on.png',
      logo2: '/icons/sort/SPORTS_on.png',
    },
    'svg1': {
      logo1: '/first/svg/sort/POPULAR1.svg',
      logo2: '/first/svg/sort/SPORTS1.svg',
    },
    'svg2': {
      logo1: '/first/svg/sort/POPULAR.svg',
      logo2: '/first/svg/sort/SPORTS.svg',
    },
  };

  const themeLogoKeys: Record<string, string[]> = {
    'image': [
      'blue-default-GameType',
      'green-default-GameType',
      'amber-purple-GameType',
    ],
    'svg1': [
      'blue-default-v01-GameType',
    ],
  }

  let themeKey = 'svg2';

  for (const key in themeLogoKeys) {
    if (themeLogoKeys[key].includes(themeHomeType.value)) {
      themeKey = key;
    }
  }

  const hotGameOptions = themeLogos[themeKey];

  return {
    hotGameOptions
  }
}

export const useBonusPoolPosition = () => {
  const { themeHomeType } = useLoadComponent();

  const bonusPool: { [key: number]: string[] } = {
    1: ['forest-green-v02-Platform', 'green-default-v02-Platform', 'blue-default-v01-GameType'],
    2: ['forest-green-v01-Platform', 'green-default-v01-Platform', 'amber-purple-v01-Platform', 'blue-default-v02-Platform'],
  };

  const showBonusPool = (positionKey: number) => bonusPool[positionKey]?.includes(themeHomeType.value) ?? false;

  return {
    showBonusPool
  }
}